/**
 * TeamSetupPanel — 组队对战配置弹窗（v4.0 · SCREENS §15）。
 * 本地组队局（2v2/3v3）的人+AI 自由搭配面板：
 *  - 座位按回合顺序 A/B 交错列出，队伍由座次派生（RULES §11），面板只
 *    决定"谁坐哪"，从不手挑队友；
 *  - 每个 AI 席位暴露 策略 × 棋力 两个正交选择器（AI_STRATEGIES §5）；
 *  - 开始即把冻结快照经 onStart 回传（调用方写入 settings 并开局）。
 */
import type { AIStrategy, Difficulty } from '@shared/game/ai';
import type { TeamMatchConfig, TeamSeatConfig } from '@shared/config/theme';
import { $settings } from '../store/settingsStore';
import { tr, trf, type DictKey } from './i18n';

const STRATEGIES: { id: AIStrategy; name: DictKey; tip: DictKey }[] = [
  { id: 'balanced', name: 'stratBalanced', tip: 'stratTipBalanced' },
  { id: 'aggressive', name: 'stratAggressive', tip: 'stratTipAggressive' },
  { id: 'defensive', name: 'stratDefensive', tip: 'stratTipDefensive' },
  { id: 'cooperative', name: 'stratCooperative', tip: 'stratTipCooperative' },
];
const DIFFICULTIES: { id: Difficulty; name: DictKey }[] = [
  { id: 'easy', name: 'easy' },
  { id: 'medium', name: 'medium' },
  { id: 'hard', name: 'hard' },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  parent?: HTMLElement
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  parent?.appendChild(e);
  return e;
}

function seat(kind: 'human' | 'ai', strategy: AIStrategy, difficulty: Difficulty): TeamSeatConfig {
  return { kind, strategy, difficulty };
}

/** 座位数：2v2 → 4，3v3 → 6（RULES §11.1）。 */
function seatCount(format: TeamMatchConfig['format']): 4 | 6 {
  return format === '3v3' ? 6 : 4;
}

// ── 快速预设（SCREENS §15.4） ──
/** 我和AI搭档：我坐 1 号位；同队协作型，对手进攻/均衡混编。 */
function presetMePartner(n: 4 | 6): TeamSeatConfig[] {
  const foes: AIStrategy[] = ['aggressive', 'balanced', 'aggressive'];
  const out: TeamSeatConfig[] = [seat('human', 'balanced', 'medium')];
  for (let i = 1; i < n; i++) {
    // 0-based 偶数回合位与我同队（A 队）。
    const mate = i % 2 === 0;
    out.push(seat('ai', mate ? 'cooperative' : (foes[(i - 1) >> 1] ?? 'balanced'), 'medium'));
  }
  return out;
}

/** 全队AI观摩：四种人格轮转，观赏性与对抗性兼具（AI_STRATEGIES §2）。 */
function presetSpectate(n: 4 | 6): TeamSeatConfig[] {
  const rotation: AIStrategy[] = ['balanced', 'aggressive', 'defensive', 'cooperative'];
  return Array.from({ length: n }, (_, i) => seat('ai', rotation[i % 4] ?? 'balanced', 'medium'));
}

/** 自定义基线：我 + 全均衡 AI（打开面板时的自由编辑起点）。 */
function presetCustom(n: 4 | 6): TeamSeatConfig[] {
  return [
    seat('human', 'balanced', 'medium'),
    ...Array.from({ length: n - 1 }, () =>
      seat('ai' as const, 'balanced' as const, 'medium' as const)
    ),
  ];
}

/** 归一化草稿：座位长度对齐赛制（保留既有前序座位配置，新增补默认 AI）。 */
function normalize(draft: TeamMatchConfig): void {
  const n = seatCount(draft.format);
  draft.seats = Array.from(
    { length: n },
    (_, i) => draft.seats[i] ?? seat('ai', 'balanced', 'medium')
  );
}

/**
 * 打开组队配置面板。`onStart` 收到深拷贝的冻结快照（此后面板内编辑不再
 * 影响对局，契约同 §4 的 settings 快照语义）。
 */
export function openTeamSetup(root: HTMLElement, onStart: (cfg: TeamMatchConfig) => void): void {
  const lang = $settings.get().language;
  const t = (k: DictKey) => tr(k, lang);
  const saved = $settings.get().teamMatch;
  const draft: TeamMatchConfig = {
    format: saved.format,
    seats: saved.seats.map((s) => ({ ...s })),
  };
  normalize(draft);

  const veil = el('div', 'modal-veil', root);
  const modal = el('div', 'modal team-modal', veil);
  el('h2', '', modal).textContent = t('teamEntry');
  el('div', 'modal-sub', modal).textContent = t('teamEntrySub');
  const body = el('div', 'modal-body', modal);
  const foot = el('div', 'modal-foot', modal);
  const cancel = el('button', 'mini-btn', foot);
  cancel.type = 'button';
  cancel.textContent = t('netClose');
  cancel.addEventListener('click', close);
  const start = el('button', 'btn btn-primary', foot);
  start.type = 'button';
  start.addEventListener('click', () => {
    const frozen: TeamMatchConfig = {
      format: draft.format,
      seats: draft.seats.map((s) => ({ ...s })),
    };
    close();
    onStart(frozen);
  });

  function close(): void {
    veil.remove();
  }
  veil.addEventListener('click', (e) => {
    if (e.target === veil) close();
  });

  function render(): void {
    body.innerHTML = '';
    const n = seatCount(draft.format);
    const humansBefore = (i: number): number =>
      draft.seats.slice(0, i).filter((s) => s.kind === 'human').length;
    const humanLabel = (ordinal: number): string =>
      ordinal <= 1 ? t('seatMe') : trf('seatPlayerN', lang, { n: ordinal });

    // ── 赛制段选 ──
    const fmtSec = el('div', 'section', body);
    el('div', 'sec-title', fmtSec).textContent = t('teamFormat');
    const fmtSeg = el('div', 'seg', fmtSec);
    for (const fmt of ['2v2', '3v3'] as const) {
      const b = el('button', `seg-btn${draft.format === fmt ? ' on' : ''}`, fmtSeg);
      b.type = 'button';
      b.textContent = fmt;
      b.addEventListener('click', () => {
        draft.format = fmt;
        normalize(draft);
        render();
      });
    }

    // ── 双队列座位（A/B 交错，§15.3） ──
    const cols = el('div', 'team-cols', body);
    const colA = el('div', 'team-col', cols);
    const colB = el('div', 'team-col', cols);
    el('div', 'team-col-head team-col-head--a', colA).textContent = t('teamSideA');
    el('div', 'team-col-head team-col-head--b', colB).textContent = t('teamSideB');

    draft.seats.forEach((seatCfg, i) => {
      const host = i % 2 === 0 ? colA : colB;
      const row = el('div', 'team-seat', host);
      el('div', 'team-seat-label', row).textContent = trf('teamSeatN', lang, { n: i + 1 });
      const controls = el('div', 'team-seat-controls', row);

      // 1) 类型选择器（我/玩家n 或 AI）。
      const typeSel = el('select', 'team-select', controls);
      const humanOpt = el('option', '', typeSel);
      humanOpt.value = 'human';
      humanOpt.textContent = humanLabel(humansBefore(i) + 1);
      const aiOpt = el('option', '', typeSel);
      aiOpt.value = 'ai';
      aiOpt.textContent = 'AI';
      typeSel.value = seatCfg.kind;
      typeSel.addEventListener('change', () => {
        seatCfg.kind = typeSel.value === 'human' ? 'human' : 'ai';
        render();
      });

      // 2)+3) 策略 × 棋力（AI 座位专用；正交双选择器，§15.2）。
      if (seatCfg.kind === 'ai') {
        const stratSel = el('select', 'team-select', controls);
        for (const s of STRATEGIES) {
          const opt = el('option', '', stratSel);
          opt.value = s.id;
          opt.textContent = t(s.name);
          opt.title = t(s.tip);
        }
        stratSel.value = seatCfg.strategy;
        const tipOf = (): string =>
          t(STRATEGIES.find((s) => s.id === seatCfg.strategy)?.tip ?? 'stratTipBalanced');
        stratSel.title = tipOf();
        stratSel.addEventListener('change', () => {
          seatCfg.strategy = stratSel.value as AIStrategy;
          stratSel.title = tipOf();
        });

        const diffSel = el('select', 'team-select', controls);
        for (const d of DIFFICULTIES) {
          const opt = el('option', '', diffSel);
          opt.value = d.id;
          opt.textContent = t(d.name);
        }
        diffSel.value = seatCfg.difficulty;
        diffSel.addEventListener('change', () => {
          seatCfg.difficulty = diffSel.value as Difficulty;
        });
      }
    });
    el('div', 'team-note', body).textContent = t('teamOrderNote');

    // ── 快速预设（§15.4） ──
    const preSec = el('div', 'section', body);
    el('div', 'sec-title', preSec).textContent = t('teamPresets');
    const preSeg = el('div', 'seg', preSec);
    const presets: { label: DictKey; build: (m: 4 | 6) => TeamSeatConfig[] }[] = [
      { label: 'presetMeAi', build: presetMePartner },
      { label: 'presetAllAi', build: presetSpectate },
      { label: 'presetCustom', build: presetCustom },
    ];
    for (const p of presets) {
      const b = el('button', 'seg-btn', preSeg);
      b.type = 'button';
      b.textContent = t(p.label);
      b.addEventListener('click', () => {
        draft.seats = p.build(n);
        render();
      });
    }

    // ── 开始：全 AI 时为观战（§15.5 明确允许——观赏性是特性） ──
    start.textContent = draft.seats.some((s) => s.kind === 'human')
      ? t('teamStart')
      : t('teamSpectate');
  }

  render();
}
